// Description: Personal Information step of the finish sign up form
// Updated: 20/08/2025
// Author: Simon Franklin

// Material UI Components
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import MuiCard from '@mui/material/Card';
import { CardActionArea, CardContent, FormControl, Input, RadioGroup, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';

// Components and Functions for the project
import { insertMaskForCpfOrCnpj, insertMaskForPhoneNumber } from '../../../utils/utils';

const FormGrid = styled(Grid)(() => ({
    display: 'flex',
    flexDirection: 'column',
}));

const Card = styled(MuiCard)(({ theme, selected }) => ({
    border: '1px solid',
    borderColor: theme.palette.divider,
    width: '100%',
    '&:hover': {
        background: 'linear-gradient(to bottom right, hsla(210, 100%, 97%, 0.5) 25%, hsla(210, 100%, 90%, 0.3) 100%)',
        borderColor: theme.palette.primary.light,
        boxShadow: '0px 2px 8px hsla(0, 0%, 0%, 0.1)',
        ...(theme.applyStyles && theme.applyStyles('dark', {
            background: 'linear-gradient(to right bottom, hsla(210, 100%, 12%, 0.2) 25%, hsla(210, 100%, 16%, 0.2) 100%)',
            borderColor: theme.palette.primary.dark,
            boxShadow: '0px 1px 8px hsla(210, 100%, 25%, 0.5)',
        })),
    },
    [theme.breakpoints.up('md')]: {
        flexGrow: 1,
        maxWidth: `calc(50% - ${theme.spacing(1)})`,
    },
    ...(selected && {
        borderColor: theme.palette.primary.light,
        ...(theme.applyStyles && theme.applyStyles('dark', {
            borderColor: theme.palette.primary.dark,
        })),
    })
}));

// Render The Component
export default function PersonalInformation({ setCheckoutData, checkoutData }) {
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const textFields = ['firstName', 'lastName'];
        const isText = textFields.includes(name);
        const textRegex = /^[A-Za-zÀ-ÿ\s]*$/;
        if (isText && !textRegex.test(value)) return;
        setCheckoutData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    return (
        <Grid container spacing={3}>
            <FormControl component="fieldset" fullWidth sx={{ padding: { xs: "0px 0px 0px 20px", sm: "0px" }}}>
                <RadioGroup
                    name="person_type"
                    value={checkoutData.person_type}
                    onChange={handleChange}
                    sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}
                >
                    <Card selected={checkoutData.person_type === 1}>
                        <CardActionArea onClick={() => setCheckoutData((prev) => ({ ...prev, person_type: 'PF' }))}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon fontSize="small" sx={{ color: checkoutData.person_type === 'PF' ? 'primary.main' : 'grey.400' }} />
                                <Typography sx={{ fontWeight: 'medium' }}>Pessoa Física</Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                    <Card selected={checkoutData.person_type === 2}>
                        <CardActionArea onClick={() => setCheckoutData((prev) => ({ ...prev, person_type: 'PJ' }))}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BusinessIcon fontSize="small" sx={{ color: checkoutData.person_type === 'PJ' ? 'primary.main' : 'grey.400' }} />
                                <Typography sx={{ fontWeight: 'medium' }}>Pessoa Jurídica</Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </RadioGroup>
            </FormControl>

            {checkoutData.person_type === "PF" ? (
                <>
                    <FormGrid item xs={12} md={6}>
                        <FormLabel htmlFor="first-name" required>Primeiro nome</FormLabel>
                        <Input variant="outlined" id="first-name" name="firstName" type="text" required size="small" value={checkoutData.firstName} onChange={handleChange} />
                    </FormGrid>
                    <FormGrid item xs={12} md={6}>
                        <FormLabel htmlFor="last-name" required>Sobrenome</FormLabel>
                        <Input variant="outlined" id="last-name" name="lastName" type="text" required size="small" value={checkoutData.lastName} onChange={handleChange} />
                    </FormGrid>
                </>
            ) : (
                <FormGrid item xs={12}>
                    <FormLabel htmlFor="first-name" required>Nome da Empresa</FormLabel>
                    <Input variant="outlined" id="first-name" name="firstName" type="text" required size="small" value={checkoutData.firstName} onChange={handleChange} />
                </FormGrid>
            )}

            <FormGrid item xs={12}>
                <FormLabel htmlFor="cpf_cnpj" required>CPF/CNPJ</FormLabel>
                <Input variant="outlined" id="cpf_cnpj" name="cpf_cnpj" type="text" required size="small" value={insertMaskForCpfOrCnpj(String(checkoutData.cpf_cnpj)) || ""} onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(/\D/g, "");
                    setCheckoutData((prev) => ({ ...prev, cpf_cnpj: onlyNumbers }));
                }} />
            </FormGrid>

            <FormGrid item xs={12}>
                <FormLabel htmlFor="telephone" required>Telefone</FormLabel>
                <Input variant="outlined" id="telephone" name="telephone" type="text" required size="small" value={checkoutData.telephone === "" ? "" : insertMaskForPhoneNumber(String(checkoutData.telephone))} onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(/\D/g, "");
                    setCheckoutData((prev) => ({ ...prev, telephone: onlyNumbers }));
                }} />
            </FormGrid>

            <FormGrid item xs={12}>
                <FormLabel htmlFor="cellular" required>Celular</FormLabel>
                <Input variant="outlined" id="cellular" name="cellular" type="text" required size="small" value={checkoutData.cellular === "" ? "" : insertMaskForPhoneNumber(String(checkoutData.cellular))} onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(/\D/g, "");
                    setCheckoutData((prev) => ({ ...prev, cellular: onlyNumbers }));
                }} />
            </FormGrid>

            <FormGrid item xs={12}>
                <FormLabel htmlFor="email" required>Email</FormLabel>
                <Input variant="outlined" id="email" name="email" type="email" required size="small" value={checkoutData.email} onChange={handleChange} />
            </FormGrid>

            <FormGrid item xs={12}>
                <FormLabel htmlFor="email_cc" required>Email Para Cobrança</FormLabel>
                <Input variant="outlined" id="email_cc" name="email_cc" type="email" required size="small" value={checkoutData.email_cc} onChange={handleChange} />
            </FormGrid>
        </Grid>
    );
}
